import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  MapPin,
  Ticket,
  LayoutGrid,
  ShieldCheck,
  Menu,
  X,
  Building2,
  Users,
  Compass,
  ExternalLink,
  CheckCircle2,
  UserCheck,
  ArrowRight,
  Globe,
  ScanLine,
  Store,
} from 'lucide-react';
import { eventInfo } from '../data';

const HOME_SECTIONS = [
  { id: 'hero', label: 'Home' },
  { id: 'impact', label: 'Impact' },
  { id: 'sectors', label: 'Focus Sectors' },
  { id: 'keynotes', label: 'Keynotes' },
  { id: 'sponsors', label: 'Sponsors' },
  { id: 'logistics', label: 'Logistics' },
] as const;

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('hero');

  const location = useLocation();
  const navigate = useNavigate();

  const normalizeAnchorId = (anchorId: string) => {
    if (anchorId === 'about') return 'impact';
    if (anchorId === 'speakers') return 'keynotes';
    if (anchorId === 'venue') return 'logistics';
    return anchorId;
  };

  // Scrollspy: update active section as user scrolls through the page
  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    // Initialize from hash if present
    const rawHash = location.hash.replace('#', '');
    const normalized = normalizeAnchorId(rawHash);
    if (normalized && HOME_SECTIONS.some((s) => s.id === normalized)) {
      setActiveSection(normalized);
    } else if (!rawHash && window.scrollY < 120) {
      setActiveSection('hero');
    }

    let timeoutId: number | undefined;

    const handleScroll = () => {
      if (timeoutId) cancelAnimationFrame(timeoutId);

      timeoutId = requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;
        const scrollHeight = document.documentElement.scrollHeight;

        // If close to the top, activate hero (Home)
        if (scrollY < 120) {
          setActiveSection('hero');
          return;
        }

        // If at the bottom of the page, activate the last section (Logistics)
        if (windowHeight + scrollY >= scrollHeight - 60) {
          setActiveSection('logistics');
          return;
        }

        // Check sections from bottom to top
        const offset = 180;
        for (let i = HOME_SECTIONS.length - 1; i >= 0; i--) {
          const section = HOME_SECTIONS[i];
          const el = document.getElementById(section.id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= offset) {
              setActiveSection(section.id);
              break;
            }
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) cancelAnimationFrame(timeoutId);
    };
  }, [location.pathname, location.hash]);

  const handleAnchorClick = (anchorId: string) => {
    closeAllMenus();
    const targetId = normalizeAnchorId(anchorId);
    setActiveSection(targetId);

    if (location.pathname !== '/') {
      navigate(targetId === 'hero' ? '/' : `/#${targetId}`);
      setTimeout(() => {
        if (targetId === 'hero') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const el = document.getElementById(targetId);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    } else {
      if (targetId === 'hero') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (window.location.hash) {
          window.history.pushState(null, '', '/');
        }
      } else {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          window.history.pushState(null, '', `/#${targetId}`);
        }
      }
    }
  };

  const getDesktopNavItemClass = (sectionId: string) => {
    const isActive = location.pathname === '/' && activeSection === sectionId;
    return `text-xs font-bold transition-all px-2.5 xl:px-3 py-1.5 rounded-xl cursor-pointer border focus:outline-hidden whitespace-nowrap ${
      isActive
        ? 'text-[#0A3D27] bg-emerald-50 border-emerald-200 shadow-xs'
        : 'text-slate-600 hover:text-[#0A3D27] hover:bg-emerald-50/60 border-transparent'
    }`;
  };

  const getMobileNavItemClass = (sectionId: string) => {
    const isActive = location.pathname === '/' && activeSection === sectionId;
    return `p-2 rounded-xl text-left text-xs font-semibold transition-all cursor-pointer border focus:outline-hidden ${
      isActive
        ? 'bg-emerald-50 border-emerald-200 text-[#0A3D27] font-bold shadow-xs'
        : 'bg-slate-50 hover:bg-emerald-50 text-slate-700 hover:text-[#0A3D27] border-transparent'
    }`;
  };

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-2xl border-b border-slate-200/90 shadow-xs">
        {/* ============================================================ */}
        {/* TIER 1: TOP EVENT RIBBON & QUICK-NAVIGATION BAR */}
        {/* ONLY: 1. Admin Login, 2. Operations Login, 3. Gate staff Login */}
        {/* ============================================================ */}
        <div className="bg-[#062B1B] border-b border-[#0A3D27] py-1.5 px-3 sm:px-6 text-xs flex flex-wrap items-center justify-between gap-2 text-emerald-100">
          {/* Left: Event Dates & Location */}
          <div className="flex items-center gap-2 text-emerald-200 font-medium truncate">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
            <span className="font-semibold text-white truncate">{eventInfo.title}</span>
            <span className="hidden sm:inline text-emerald-500">•</span>
            <span className="hidden sm:inline text-emerald-200/90">{eventInfo.dates.display}</span>
            <span className="hidden md:inline text-emerald-500">•</span>
            <button
              type="button"
              onClick={() => handleAnchorClick('logistics')}
              className="hidden md:inline text-[#FAF5EA] font-semibold hover:underline cursor-pointer text-left truncate"
            >
              {eventInfo.venue.name}
            </button>
          </div>

          {/* Right: Customer Portals & Applications */}
          <div className="flex items-center gap-1.5 sm:gap-2 ml-auto text-[11px] flex-wrap">
            {/* 1. Apply: Speakers / Pods / Press */}
            <NavLink
              to="/apply"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg font-bold transition-all border ${
                  isActive || location.pathname === '/speakers/apply'
                    ? 'bg-[#C5A059] text-slate-950 border-[#C5A059] shadow-xs'
                    : 'text-[#FAF5EA] bg-[#C5A059]/20 hover:bg-[#C5A059]/35 border-[#C5A059]/40'
                }`
              }
              title="Apply for Speakers, Student Innovation Pods, or Media Press Accreditation"
            >
              <Sparkles className="w-3 h-3 text-[#E6C687]" />
              <span>Apply: Speakers / Pods / Press</span>
            </NavLink>

            {/* 2. Exhibitor Portal */}
            <NavLink
              to="/exhibitor-portal"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg font-bold transition-all border ${
                  isActive
                    ? 'bg-purple-400 text-slate-950 border-purple-300 shadow-xs'
                    : 'text-purple-100 bg-purple-900/40 hover:bg-purple-900/60 border-purple-500/40'
                }`
              }
              title="Exhibitor Portal (Stall Fascia, Badges & Directory)"
            >
              <Store className="w-3 h-3 text-purple-300" />
              <span>Exhibitor Portal</span>
            </NavLink>

            {/* 3. Visitor Portal */}
            <NavLink
              to="/my-iiab"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg font-bold transition-all border ${
                  isActive
                    ? 'bg-sky-400 text-slate-950 border-sky-300 shadow-xs'
                    : 'text-sky-100 bg-sky-900/40 hover:bg-sky-900/60 border-sky-500/40'
                }`
              }
              title="Visitor Portal (My IIAB, Digital Badge & B2B Networking)"
            >
              <Ticket className="w-3 h-3 text-sky-300" />
              <span>Visitor Portal</span>
            </NavLink>
          </div>
        </div>

        {/* ============================================================ */}
        {/* TIER 2: PRIMARY NAVBAR (DIRECT LANDING PAGE LINKS, NO DROPDOWNS) */}
        {/* ORDER: 1. Home, 2. Impact, 3. Focus Sectors, 4. Keynotes, 5. Sponsors, 6. Logistics | 7. Visitor Registration, 8. Book a stall */}
        {/* ============================================================ */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
          {/* Brand & Edition Badge */}
          <Link
            to="/"
            className="flex items-center gap-3 group shrink-0"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                handleAnchorClick('hero');
              } else {
                closeAllMenus();
              }
            }}
          >
            <img
              src="/logo/iiab_logo.png"
              alt="IIAB 2027 Logo"
              className="h-9 sm:h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-200"
            />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base font-black tracking-tight text-slate-900 group-hover:text-[#0A3D27] transition-colors">
                  IIAB 2027
                </span>
                <span className="text-[10px] font-extrabold bg-emerald-50 text-[#0A3D27] border border-emerald-200 px-2 py-0.5 rounded-full">
                  {eventInfo.edition}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-[#0A3D27] inline shrink-0" />
                <span className="truncate">{eventInfo.venue.shortName}, Mumbai • {eventInfo.dates.display}</span>
              </p>
            </div>
          </Link>

          {/* MAIN DIRECT NAVIGATION LINKS (EXACT SPECIFIED ORDER) */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2 text-xs font-semibold text-slate-600">
            {/* 1. Home */}
            {/* 2. Impact (Snap to Strategic Market Growth) */}
            {/* 3. Focus Sectors (Snap to Exhibition Profiles) */}
            {/* 4. Keynotes (Snap to Leadership & Keynote Directory) */}
            {/* 5. Sponsors (Snap to Sponsors & Global Partners) */}
            {/* 6. Logistics (Snap to Venue, Accommodation & Travel Logistics) */}
            {HOME_SECTIONS.map((sec) => (
              <button
                key={sec.id}
                type="button"
                onClick={() => handleAnchorClick(sec.id)}
                className={getDesktopNavItemClass(sec.id)}
              >
                {sec.label}
              </button>
            ))}

            <span className="text-slate-300 mx-1">|</span>

            {/* 7. Register to Attend */}
            <NavLink
              to="/register"
              className={({ isActive }) =>
                `text-xs font-bold transition-all px-3 py-1.5 rounded-xl cursor-pointer flex items-center gap-1.5 border focus:outline-hidden whitespace-nowrap ${
                  isActive || location.pathname === '/my-pass'
                    ? 'text-cyan-800 bg-cyan-50 border-cyan-200 shadow-xs'
                    : 'text-slate-600 hover:text-cyan-800 hover:bg-cyan-50/60 border-transparent'
                }`
              }
            >
              <Ticket className="w-3.5 h-3.5 text-cyan-600" />
              <span>Register to Attend</span>
            </NavLink>

            {/* 8. Book a stall */}
            <NavLink
              to="/floorplan"
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-extrabold transition-all shadow-md shadow-emerald-950/20 cursor-pointer ml-1 whitespace-nowrap"
            >
              <Building2 className="w-3.5 h-3.5" />
              <span>Book a stall</span>
            </NavLink>
          </nav>

          {/* Mobile Menu Trigger */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Navigation Menu"
              className="p-2 text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl cursor-pointer transition-colors"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* ============================================================ */}
      {/* MOBILE DRAWER (MATCHING USER FLOWS) */}
      {/* ============================================================ */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-6 py-5 space-y-5 animate-in slide-in-from-top-2 duration-200 shadow-xl">
          {/* Section 1: Public Website (Ordered to match page flow) */}
          <div className="space-y-2">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Globe className="w-3 h-3 text-[#0A3D27]" />
              <span>1. Public Sections</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs font-semibold text-slate-700">
              {HOME_SECTIONS.map((sec) => (
                <button
                  key={sec.id}
                  type="button"
                  onClick={() => handleAnchorClick(sec.id)}
                  className={getMobileNavItemClass(sec.id)}
                >
                  {sec.label}
                </button>
              ))}
              <Link
                to="/terms"
                onClick={closeAllMenus}
                className="p-2 bg-slate-50 hover:bg-emerald-50 rounded-xl hover:text-[#0A3D27] text-slate-700 text-xs font-semibold"
              >
                Terms & Legal
              </Link>
            </div>
          </div>

          {/* Section 2: Register to Attend & Book a stall */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Ticket className="w-3 h-3 text-cyan-600" />
              <span>2. Registrations & Stall Booking</span>
            </div>
            <div className="space-y-2">
              <Link
                to="/register"
                onClick={closeAllMenus}
                className="flex items-center justify-between p-2.5 bg-cyan-50/60 border border-cyan-200 rounded-xl text-xs font-bold text-cyan-900 hover:bg-cyan-100/60"
              >
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-cyan-600" />
                  <div>
                    <div>Register to Attend</div>
                    <div className="text-[10px] text-slate-500 font-normal">Pricing tiers & delegate registration</div>
                  </div>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-cyan-600" />
              </Link>

              <Link
                to="/floorplan"
                onClick={closeAllMenus}
                className="flex items-center justify-between p-2.5 bg-[#0A3D27] text-white rounded-xl text-xs font-bold hover:bg-[#062B1B]"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-emerald-300" />
                  <div>
                    <div>Book a stall</div>
                    <div className="text-[10px] text-emerald-200 font-normal">Interactive floor plan & stall reservations</div>
                  </div>
                </div>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Section 3: Portals & Applications */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-[#785613]" />
              <span>3. Portals &amp; Applications</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <Link
                to="/apply"
                onClick={closeAllMenus}
                className="p-2.5 bg-[#FAF5EA] border border-[#E6C687] text-[#785613] rounded-xl font-bold flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-[#785613]" />
                <span>Apply: Speakers / Pods / Press</span>
              </Link>

              <Link
                to="/exhibitor-portal"
                onClick={closeAllMenus}
                className="p-2.5 bg-purple-50 border border-purple-200 text-purple-900 rounded-xl font-bold flex items-center gap-2"
              >
                <Store className="w-4 h-4 text-purple-600" />
                <span>Exhibitor Portal</span>
              </Link>

              <Link
                to="/my-iiab"
                onClick={closeAllMenus}
                className="p-2.5 bg-sky-50 border border-sky-200 text-sky-900 rounded-xl font-bold flex items-center gap-2"
              >
                <Ticket className="w-4 h-4 text-sky-600" />
                <span>Visitor Portal</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;

